import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { path } from 'ramda';
import { Carousel, Col, Row, Skeleton, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CarouselRef } from 'antd/lib/carousel';

import { useTheme } from 'common/styling/theme';
import { useIntl } from 'common/config/i18n';
import CardSendCommunication from 'components/molecules/card-send-communication';
import Button from 'components/molecules/button';
import {
  SendCommunicationSelectors,
  stationsTemplatesPreviewActions,
  stationsTemplatesPreviewSelectors,
  UserInfoSelectors,
} from 'common/config/store';
import {
  checkIfAllStationsConcerned,
  prepareFetchStationsTemplatesPreviewParams,
  sortImpactedStations,
} from 'common/utils/send-communication';
import { Constants } from 'core';
import { SaveCommunicationError } from 'types';

import { NextArrow, PrevArrow } from './arrows';
import Frame from './frame';

const { Paragraph } = Typography;

interface Props {
  onConfirm: () => void;
  isSaveCommunicationLoading: boolean;
  isBulkCommunication?: boolean;
  saveCommunicationError?: SaveCommunicationError;
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledContent = styled.div`
  height: 880px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow-y: scroll;
`;

const StyledListButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`;

const SubTitle = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'bold'])};
`;

const ModalMsgsPreview: React.FC<Props> = ({
  onConfirm,
  isSaveCommunicationLoading,
  isBulkCommunication,
  saveCommunicationError,
}) => {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const [currIndex, setCurrIndex] = useState<number>(0);
  const [isSwiping, setIsSwiping] = useState<boolean>(false);
  const [templatesPreviewErrorMessage, setTemplatesPreviewErrorMessage] = useState<string | undefined>('');
  const carouselRef = useRef<CarouselRef>(null);

  const fetchStatus = useSelector(stationsTemplatesPreviewSelectors.statusSelector);
  const stationsTemplates = useSelector(stationsTemplatesPreviewSelectors.stationsTemplatesSelector);
  const fullUserName = useSelector(UserInfoSelectors.fullNameSelector);

  const stationsConcerned = useSelector(SendCommunicationSelectors.stationsConcernedSelector);
  const selectedServices = useSelector(SendCommunicationSelectors.selectedServicesSelector);
  const formValuesFirstStep = useSelector(SendCommunicationSelectors.firstStepFormValuesSelector);
  const formValuesSecondStep = useSelector(SendCommunicationSelectors.secondStepFormValuesSelector);

  useEffect(() => {
    if (selectedServices && formValuesFirstStep && formValuesSecondStep) {
      const selectedServicesMapped = selectedServices.map(({ serviceName, circulationDate }) => ({
        serviceName,
        serviceDate: circulationDate,
      }));
      const fetchStationsTemplatesParams = prepareFetchStationsTemplatesPreviewParams(
        selectedServicesMapped,
        formValuesFirstStep,
        formValuesSecondStep,
        stationsConcerned,
        fullUserName,
        isBulkCommunication
      );
      dispatch(
        stationsTemplatesPreviewActions.fetchStationsTemplatesPreview(fetchStationsTemplatesParams, (result) => {
          if (result?.cancelled || result?.error) {
            setTemplatesPreviewErrorMessage(result?.error?.content?.message);
          }
        })
      );
    }
  }, [
    dispatch,
    formValuesFirstStep,
    formValuesSecondStep,
    selectedServices,
    stationsConcerned,
    fullUserName,
    isBulkCommunication,
  ]);

  const isAllStationsConcerned = checkIfAllStationsConcerned(formValuesFirstStep);
  const nmbrOfImpactedStations = formValuesFirstStep?.impactedStations?.length;

  const next = () => {
    carouselRef?.current?.next();
    setCurrIndex(currIndex + 1);
  };
  const previous = () => {
    carouselRef?.current?.prev();
    setCurrIndex(currIndex - 1);
  };

  const CarouselItem = ({ uicStation }: { uicStation?: string }) => {
    const carouselContentStyle: CSSProperties = {
      padding: 10,
      height: '820px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'visible',
    };
    const cardWrapperStyle: CSSProperties = {
      padding: 0,
      backgroundColor: 'transparent',
    };
    const cardTitleStyle: CSSProperties = {
      paddingTop: '10px',
      paddingBottom: '10px',
    };

    let title = '';
    let currStationTemplates = null;
    if (isAllStationsConcerned) {
      title = t('sendCommunication', 'msgsPreview', 'titleAllStations');
      currStationTemplates = stationsTemplates?.[0];
    } else if (uicStation) {
      const currStation = stationsConcerned?.find((station) => station.uicStation === uicStation);
      title = t('sendCommunication', 'msgsPreview', 'titleImpactedStations', { name: currStation?.shortCode });
      currStationTemplates = stationsTemplates?.find(
        (stationTemplates) => stationTemplates.stationUicCode === uicStation
      );
    }

    const hasTemplates =
      currStationTemplates?.emailTemplate?.body ||
      currStationTemplates?.whatsAppTemplate?.body ||
      currStationTemplates?.smsTemplate?.body;

    return (
      <div>
        <CardSendCommunication title={title} wrraperStyle={cardWrapperStyle} titleStyle={cardTitleStyle}>
          {!isSwiping && hasTemplates && (
            <Row>
              {currStationTemplates?.emailTemplate?.body && (
                <Col style={{ ...carouselContentStyle, width: '60%' }}>
                  {currStationTemplates?.emailTemplate?.object && (
                    <SubTitle>{currStationTemplates?.emailTemplate?.object}</SubTitle>
                  )}
                  <Frame srcDoc={currStationTemplates?.emailTemplate?.body} />
                </Col>
              )}
              <Col style={{ ...carouselContentStyle, width: '40%' }}>
                {currStationTemplates?.smsTemplate?.body && (
                  <div style={{ height: '50%' }}>
                    <Frame
                      srcDoc={`
                        <!DOCTYPE html>
                          <head>
                            <style>
                              body {
                                white-space: pre-wrap;
                              }
                            </style>
                          </head>
                          <body>${currStationTemplates?.smsTemplate?.body}</body>
                        </html>
                    `}
                    />
                  </div>
                )}
                {currStationTemplates?.whatsAppTemplate?.body && (
                  <div style={{ height: '100%' }}>
                    <Frame srcDoc={currStationTemplates?.whatsAppTemplate?.body} />
                  </div>
                )}
              </Col>
            </Row>
          )}
          {!isSwiping && !hasTemplates && (
            <Row style={{ ...carouselContentStyle, width: '100%', justifyContent: 'center' }}>
              <Col style={{ width: '100%', textAlign: 'center' }}>
                <h1>{t('sendCommunication', 'msgsPreview', 'textNoTemplates')}</h1>
              </Col>
            </Row>
          )}
          {isSwiping && (
            <Row style={{ ...carouselContentStyle, justifyContent: 'center', alignItems: 'center' }}>
              <Skeleton avatar active style={{ width: '50%' }} />
            </Row>
          )}
        </CardSendCommunication>
      </div>
    );
  };

  const impactedStationsSorted = sortImpactedStations(formValuesFirstStep.impactedStations, stationsConcerned);

  return (
    <StyledContainer>
      <StyledContent>
        {fetchStatus === 'success' && (
          <>
            <PrevArrow
              onClick={previous}
              disabled={isSwiping || isAllStationsConcerned || nmbrOfImpactedStations === 1 || currIndex === 0}
            />
            <Carousel
              dots={false}
              ref={carouselRef}
              beforeChange={() => setIsSwiping(true)}
              afterChange={() => setIsSwiping(false)}
            >
              {isAllStationsConcerned ? (
                <CarouselItem />
              ) : (
                impactedStationsSorted?.map((uicStation) => <CarouselItem key={uicStation} uicStation={uicStation} />)
              )}
            </Carousel>
            <NextArrow
              onClick={next}
              disabled={
                isSwiping ||
                isAllStationsConcerned ||
                nmbrOfImpactedStations === 1 ||
                currIndex >= nmbrOfImpactedStations - 1
              }
            />
          </>
        )}
        {fetchStatus === 'loading' && (
          <Row align="middle" justify="center" style={{ width: '50%' }}>
            <Skeleton avatar active />
          </Row>
        )}
      </StyledContent>
      <StyledListButtonWrapper>
        <Button
          backgroundColor={path(['colors', 'secondary', 'main'], theme)}
          onClick={() => onConfirm()}
          disabled={isSaveCommunicationLoading || !!templatesPreviewErrorMessage}
          loading={isSaveCommunicationLoading}
        >
          {t('Buttons', 'SEND_BUTTON')}
        </Button>
        {saveCommunicationError?.hasError && (
          <Paragraph type="danger" style={{ margin: 'auto', marginLeft: 15, fontWeight: 'bold' }}>
            {saveCommunicationError?.message || t('confirmation', 'TEXT_CONF_PAGE_KO_1')}
          </Paragraph>
        )}
        {!!templatesPreviewErrorMessage && (
          <Paragraph type="danger" style={{ margin: 'auto', marginLeft: 15, fontWeight: 'bold' }}>
            {templatesPreviewErrorMessage === Constants.SELECTED_CARRIAGES_REQUIRED &&
              t('confirmation', 'TEXT_CONF_PAGE_KO_STEP_1')}
            {templatesPreviewErrorMessage !== Constants.SELECTED_CARRIAGES_REQUIRED && t('defaultError')}
          </Paragraph>
        )}
      </StyledListButtonWrapper>
    </StyledContainer>
  );
};

export default ModalMsgsPreview;
